import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { loginAPI } from '../../services';
import { tokenStorage } from '../../utils/tokenStorage';

// Async thunk for login
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (credentials, { rejectWithValue, dispatch }) => {
    try {
      const result = await loginAPI.login(credentials);
      
      if (result.error) {
        // Handle different error formats from backend
        let errorMessage = 'Login failed';
        
        if (result.error?.data?.non_field_errors && Array.isArray(result.error.data.non_field_errors)) {
          // Handle non_field_errors array (e.g., "No account found with this email.")
          errorMessage = result.error.data.non_field_errors[0];
        } else if (result.error?.data?.message) {
          errorMessage = result.error.data.message;
        } else if (result.error?.message) {
          errorMessage = result.error.message;
        } else if (result.error?.data?.detail) {
          errorMessage = result.error.data.detail;
        }
        
        return rejectWithValue(errorMessage);
      }
      
      // Handle different token response formats
      const response = result.response;
      const accessToken = response?.access || response?.access_token || response?.accessToken || response?.token;
      const refreshToken = response?.refresh || response?.refresh_token || response?.refreshToken;
      const user = response?.user || { role: response?.role };

      if (accessToken) {
        // Store tokens using utility
        tokenStorage.setTokens(accessToken, refreshToken, user);
        
        // Fetch user details after successful login
        dispatch(fetchUserDetails());
        
        return {
          accessToken,
          refreshToken,
          user
        };
      } else {
        return rejectWithValue('No access token received');
      }
      
    } catch (error) {
      const errorMessage = error.message || 'Login failed';
      return rejectWithValue(errorMessage);
    }
  }
);

// Fetch user basic details
export const fetchUserDetails = createAsyncThunk(
  'auth/fetchUserDetails',
  async (_, { rejectWithValue }) => {
    try {
      const result = await loginAPI.getUserBasicDetails();
      
      if (result.error) {
        const errorMessage = result.error?.data?.message || result.error?.message || 'Failed to fetch user details';
        return rejectWithValue(errorMessage);
      }
      
      const userData = result.response;
      
      // Extract user data from the nested structure
      const userProfile = {
        id: userData.data.profile.id,
        name: userData.data.profile.name,
        email: userData.data.profile.display_email,
        admin_role: userData.data.profile.admin_role,
        role: userData.data.role
      };
      
      return userProfile;
      
    } catch (error) {
      const errorMessage = error.message || 'Failed to fetch user details';
      return rejectWithValue(errorMessage);
    }
  }
);

// Refresh token action
export const refreshTokenUser = createAsyncThunk(
  'auth/refreshToken',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const refreshToken = state.auth.refreshToken || tokenStorage.getRefreshToken();
      
      if (!refreshToken) {
        return rejectWithValue('No refresh token available');
      }

      const result = await loginAPI.refreshToken(refreshToken);
      
      if (result.error) {
        const errorMessage = result.error?.data?.message || result.error?.message || 'Token refresh failed';
        return rejectWithValue(errorMessage);
      }
      
      // Handle different token response formats
      const response = result.response;
      const accessToken = response?.access || response?.access_token || response?.accessToken || response?.token;
      const newRefreshToken = response?.refresh || response?.refresh_token || response?.refreshToken;
      const user = response?.user || state.auth.user || {};

      if (accessToken) {
        // Store new tokens using utility
        tokenStorage.setTokens(accessToken, newRefreshToken || refreshToken, user);
        
        return {
          accessToken,
          refreshToken: newRefreshToken || refreshToken,
          user
        };
      } else {
        return rejectWithValue('No access token received from refresh');
      }
      
    } catch (error) {
      const errorMessage = error.message || 'Token refresh failed';
      return rejectWithValue(errorMessage);
    }
  }
);

// Logout action
export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async () => {
    tokenStorage.clearTokens();
    return null;
  }
);

const initialState = {
  user: tokenStorage.getUser(),
  accessToken: tokenStorage.getAccessToken(),
  refreshToken: tokenStorage.getRefreshToken(),
  isLoading: false,
  isAuthenticated: tokenStorage.isAuthenticated(),
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Login cases
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user || {};
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.error = action.payload;
      })
      // Fetch user details cases
      .addCase(fetchUserDetails.pending, (state) => {
        // Don't set loading for user details fetch to avoid UI blocking
      })
      .addCase(fetchUserDetails.fulfilled, (state, action) => {
        state.user = action.payload;
        // Update stored user data
        tokenStorage.setTokens(state.accessToken, state.refreshToken, action.payload);
      })
      .addCase(fetchUserDetails.rejected, (state, action) => {
        console.error('Failed to fetch user details:', action.payload);
      })
      // Refresh token cases
      .addCase(refreshTokenUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(refreshTokenUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user || state.user;
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        state.isAuthenticated = true;
      })
      .addCase(refreshTokenUser.rejected, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
      })
      // Logout cases
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
      });
  },
});

export default authSlice.reducer;
